/**
 * 記事末尾の共有導線(partials/post-share.hbs)の動作。
 *
 * - Copy title & URL: 記事のタイトルと絶対 URL を「タイトル\nURL」の形でクリップボードへ書く
 * - Copy for AI: 記事本文(.gh-content)を Markdown に変換し、タイトル・URL・公開日と共にクリップボードへ書く。
 *   ボタンを押すと選択肢が開き、「この記事だけ」か「関連記事も含める」かを選ぶ。関連記事は
 *   scripts/hyperstrata-sync.mjs が生成する assets/graph.json の引用(refs)と研究者の注釈(inferredRefs)を
 *   双方向にたどって集め、著者の引用と関係の強い順(inferredRefs[].strength)に上位 RELATED_LIMIT 件へ絞る。本文は現在記事・関連記事とも記事ページを同じ origin で fetch して
 *   サーバー出力の HTML から取り出す(X の埋め込みを widgets.js が iframe に置き換える前の本文を得るため)
 * - Post your thoughts: 押すと X / Bluesky / Threads の intent リンクの選択肢が開く。
 *   リンク自体はパーシャルだけで完結し、このスクリプトは選択肢の開閉だけを担う
 * - 選択肢(data-share-options)はボタンの下に重ねて出し、同時に 1 つだけ開く。外側のクリックと Escape で閉じる
 *
 * 設計上の注意:
 * - Ghost は本文を HTML でしか配信しないため、Markdown への変換はブラウザ側で DOM を歩いて行う。
 *   Ghost のカード(画像・ブックマーク・埋め込み・コード・コールアウト・トグル・動画・ボタン)は
 *   kg-* クラスで見分け、未知のブロックは子要素を順に出す。サインアップカードは本文ではないので出さない。
 *   X の埋め込みは投稿のテキスト・投稿者・日付・投稿 URL を「X への投稿」と明示した引用にする
 * - graph.json の記事 URL は本番の絶対 URL なので、ローカル確認環境でも動くように pathname だけを使って
 *   現在の origin から fetch する
 * - Safari はユーザー操作から非同期に離れた後の clipboard.writeText を拒否するため、関連記事の取得を
 *   待つ場合は ClipboardItem に Promise を渡して書く。ClipboardItem に Promise を渡せないブラウザ
 *   (古い Firefox など)では取得を待ってから writeText で書く
 * - DOM に依存しない部分(htmlToMarkdown / collectRelated / selectRelated / buildDocument / buildCopyText / extractPost / loadPostContent / mapWithLimit)は
 *   window.HanataneShare に公開し、scripts/post-share.test.mjs から検証する
 */
(function () {
    /** コピー完了・失敗の表示を戻すまでの時間(ミリ秒) */
    const FEEDBACK_MS = 2000;

    /** 関連記事のページを同時に fetch する上限。関連記事が多い記事でもサーバーへ一斉に要求しないようにする */
    const MAX_CONCURRENT_FETCHES = 4;

    /**
     * 「AIに渡す(関連記事つき)」に含める関連記事の上限。年間の振り返りのように関係の多い記事(最大で 15 件)でも
     * 文書が長くなりすぎないよう、著者の引用と関係の強い順(selectRelated)に絞る(#56)。
     */
    const RELATED_LIMIT = 8;

    /** 著者が本文リンクで作った引用を表す関係の種類(collectRelated が付ける)。機械の推定より優先する */
    const CITATION_TYPES = ['cites', 'cited-by'];

    /** 本文として出さない要素 */
    const SKIP_TAGS = ['SCRIPT', 'STYLE', 'TEMPLATE', 'FORM', 'INPUT', 'BUTTON', 'NOSCRIPT', 'SVG'];

    /** 段落を区切るブロック要素 */
    const BLOCK_TAGS = [
        'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DETAILS', 'DIV', 'DL', 'FIGURE', 'FIGCAPTION', 'FOOTER',
        'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'HEADER', 'HR', 'IFRAME', 'LI', 'MAIN', 'NAV', 'OL', 'P', 'PRE',
        'SECTION', 'TABLE', 'UL', 'VIDEO'
    ];

    /** 改行(br)を表す一時的な印。インラインの空白をまとめた後に "  \n" に置き換える */
    const BR_MARK = ' ';

    /**
     * 注釈の関係を相手側から見た種類に反転する(現在記事を continues している記事は continued-by)。
     */
    const REVERSE_TYPE = {
        continues: 'continued-by',
        revisits: 'revisited-by',
        updates: 'updated-by'
    };

    /** 関係の種類を AI に渡す文書での説明にする(主語は関連記事) */
    const RELATION_LABELS = {
        cites: 'cited by the main post',
        'cited-by': 'cites the main post',
        continues: 'continued by the main post',
        revisits: 'revisited by the main post',
        updates: 'updated by the main post',
        'continued-by': 'continues the main post',
        'revisited-by': 'revisits the main post',
        'updated-by': 'updates the main post'
    };

    // -----------------------------------------------------------------------
    // HTML → Markdown
    // -----------------------------------------------------------------------

    /**
     * インラインの文字列の空白をまとめ、改行の印を Markdown の改行(行末 2 スペース)にする。
     *
     * @param {string} text
     * @returns {string}
     */
    function normalizeInline(text) {
        return text
            .replace(/[ \t\r\n]+/g, ' ')
            .split(BR_MARK)
            .map(function (part) {
                return part.trim();
            })
            .join('  \n')
            .trim();
    }

    /**
     * 要素の子をインラインとして Markdown にする(空白はまとめない。呼び出し側が normalizeInline する)。
     *
     * @param {Node} parent
     * @returns {string}
     */
    function inlineChildren(parent) {
        return Array.prototype.map.call(parent.childNodes, inline).join('');
    }

    /**
     * インラインの 1 ノードを Markdown にする。
     *
     * @param {Node} node
     * @returns {string}
     */
    function inline(node) {
        if (node.nodeType === 3) {
            return node.nodeValue;
        }
        if (node.nodeType !== 1 || SKIP_TAGS.indexOf(node.tagName) >= 0) {
            return '';
        }
        const inner = inlineChildren(node);
        switch (node.tagName) {
        case 'BR':
            return BR_MARK;
        case 'STRONG':
        case 'B':
            return inner.trim() ? '**' + inner.trim() + '**' : '';
        case 'EM':
        case 'I':
            return inner.trim() ? '*' + inner.trim() + '*' : '';
        case 'DEL':
        case 'S':
            return inner.trim() ? '~~' + inner.trim() + '~~' : '';
        case 'CODE':
            return '`' + node.textContent + '`';
        case 'A': {
            const href = node.getAttribute('href');
            return href ? '[' + inner.trim() + '](' + href + ')' : inner;
        }
        case 'IMG':
            return image(node);
        default:
            return inner;
        }
    }

    /**
     * 要素の中身をインラインとして Markdown にし、空白をまとめる。
     *
     * @param {Node} parent
     * @returns {string}
     */
    function inlineText(parent) {
        return normalizeInline(inlineChildren(parent));
    }

    /**
     * 画像を Markdown の画像記法にする。
     *
     * @param {Element} img
     * @returns {string}
     */
    function image(img) {
        const src = img.getAttribute('src') || '';
        return src ? '![' + (img.getAttribute('alt') || '') + '](' + src + ')' : '';
    }

    /**
     * 要素が段落を区切るブロックかどうか。kg-* カードの div も含む。
     *
     * @param {Element} el
     * @returns {boolean}
     */
    function isBlock(el) {
        return BLOCK_TAGS.indexOf(el.tagName) >= 0;
    }

    /**
     * 親の子ノードを Markdown のブロック(段落・見出し・リスト等)の配列にする。
     * 連続するインラインのノードは 1 つの段落にまとめる。
     *
     * @param {Node} parent
     * @returns {string[]}
     */
    function renderBlocks(parent) {
        const blocks = [];
        let buffer = '';
        const flush = function () {
            const text = normalizeInline(buffer);
            if (text) {
                blocks.push(text);
            }
            buffer = '';
        };
        Array.prototype.forEach.call(parent.childNodes, function (node) {
            if (node.nodeType === 1 && isBlock(node)) {
                flush();
                renderBlock(node).forEach(function (block) {
                    if (block) {
                        blocks.push(block);
                    }
                });
                return;
            }
            buffer += inline(node);
        });
        flush();
        return blocks;
    }

    /**
     * リスト(ul / ol)を Markdown にする。入れ子は親の印の幅だけ字下げする。
     *
     * @param {Element} list
     * @param {string} indent 現在の字下げ
     * @returns {string}
     */
    function renderList(list, indent) {
        const ordered = list.tagName === 'OL';
        const start = parseInt(list.getAttribute('start') || '1', 10);
        const lines = [];
        let index = 0;
        Array.prototype.forEach.call(list.children, function (item) {
            if (item.tagName !== 'LI') {
                return;
            }
            const marker = ordered ? String(start + index) + '. ' : '- ';
            index += 1;
            const nested = [];
            const clone = item.cloneNode(true);
            Array.prototype.slice.call(clone.children).forEach(function (child) {
                if (child.tagName === 'UL' || child.tagName === 'OL') {
                    clone.removeChild(child);
                }
            });
            Array.prototype.forEach.call(item.children, function (child) {
                if (child.tagName === 'UL' || child.tagName === 'OL') {
                    nested.push(renderList(child, indent + ' '.repeat(marker.length)));
                }
            });
            lines.push(indent + marker + inlineText(clone));
            nested.forEach(function (block) {
                lines.push(block);
            });
        });
        return lines.join('\n');
    }

    /**
     * コードブロック(pre)を言語付きのフェンスにする。
     *
     * @param {Element} pre
     * @returns {string}
     */
    function renderCode(pre) {
        const code = pre.querySelector('code') || pre;
        const match = /(?:^|\s)language-([\w-]+)/.exec(code.className || '');
        const lang = match ? match[1] : '';
        return '```' + lang + '\n' + code.textContent.replace(/\n$/, '') + '\n```';
    }

    /**
     * 表を Markdown のパイプ表にする。
     *
     * @param {Element} table
     * @returns {string}
     */
    function renderTable(table) {
        const rows = Array.prototype.map.call(table.querySelectorAll('tr'), function (tr) {
            return Array.prototype.map.call(tr.querySelectorAll('th, td'), function (cell) {
                return inlineText(cell).replace(/\|/g, '\\|').replace(/\n/g, ' ');
            });
        }).filter(function (cells) {
            return cells.length > 0;
        });
        if (rows.length === 0) {
            return '';
        }
        const line = function (cells) {
            return '| ' + cells.join(' | ') + ' |';
        };
        const header = rows[0];
        const separator = header.map(function () {
            return '---';
        });
        return [line(header), line(separator)].concat(rows.slice(1).map(line)).join('\n');
    }

    /**
     * figure の中のキャプションを取り出す。
     *
     * @param {Element} figure
     * @returns {string}
     */
    function caption(figure) {
        const figcaption = figure.querySelector(':scope > figcaption');
        return figcaption ? inlineText(figcaption) : '';
    }

    /**
     * 要素の中身を装飾やリンクを除いた素のテキストにする。br は改行、各行の空白はまとめる。
     *
     * @param {Element} el
     * @returns {string}
     */
    function plainText(el) {
        const clone = el.cloneNode(true);
        Array.prototype.forEach.call(clone.querySelectorAll('br'), function (br) {
            br.parentNode.replaceChild(clone.ownerDocument.createTextNode('\n'), br);
        });
        return clone.textContent.split('\n').map(function (line) {
            return line.replace(/\s+/g, ' ').trim();
        }).filter(Boolean).join('\n');
    }

    /**
     * X の埋め込み(Ghost が保存する oEmbed の blockquote.twitter-tweet)を、X への投稿であることを明示した引用にする。
     * blockquote は「<p>本文</p>— 投稿者 (@id) <a href="投稿 URL">日付</a>」の形で、最後の a が投稿 URL と日付、
     * p の後から最後の a までのテキストが投稿者になる。投稿 URL は ref などのクエリを落とし、x.com に揃える。
     *
     * @param {Element} blockquote
     * @returns {string}
     */
    function renderTweet(blockquote) {
        const body = blockquote.querySelector('p');
        const links = blockquote.querySelectorAll(':scope > a');
        const statusLink = links.length > 0 ? links[links.length - 1] : null;
        const author = Array.prototype.filter.call(blockquote.childNodes, function (node) {
            return node !== body && node !== statusLink;
        }).map(function (node) {
            return node.textContent;
        }).join('').replace(/\s+/g, ' ').replace(/^\s*[—–-]\s*/, '').trim();
        const date = statusLink ? statusLink.textContent.trim() : '';
        let url = '';
        if (statusLink && statusLink.getAttribute('href')) {
            const parsed = new URL(statusLink.getAttribute('href'), 'https://x.com/');
            url = 'https://x.com' + parsed.pathname;
        }
        const lines = (body ? plainText(body) : '').split('\n').filter(Boolean);
        lines.push('');
        lines.push('— ' + author + ' の X への投稿' + (date ? ' (' + date + ')' : ''));
        if (url) {
            lines.push(url);
        }
        return lines.map(function (line) {
            return line ? '> ' + line : '>';
        }).join('\n');
    }

    /**
     * Ghost のカード(figure / div.kg-card)を Markdown のブロック配列にする。
     *
     * @param {Element} el
     * @returns {string[]}
     */
    function renderCard(el) {
        const classes = el.className || '';
        if (/\bkg-signup-card\b/.test(classes)) {
            return [];
        }
        if (/\bkg-code-card\b/.test(classes)) {
            const pre = el.querySelector('pre');
            return [pre ? renderCode(pre) : '', caption(el)];
        }
        if (/\bkg-bookmark-card\b/.test(classes)) {
            const link = el.querySelector('a.kg-bookmark-container');
            const title = el.querySelector('.kg-bookmark-title');
            const description = el.querySelector('.kg-bookmark-description');
            if (!link) {
                return renderBlocks(el);
            }
            const href = link.getAttribute('href') || '';
            const text = title ? inlineText(title) : href;
            const lines = ['[' + text + '](' + href + ')'];
            if (description && inlineText(description)) {
                lines.push(inlineText(description));
            }
            return [lines.join('\n'), caption(el)];
        }
        if (/\bkg-callout-card\b/.test(classes)) {
            const emoji = el.querySelector('.kg-callout-emoji');
            const text = el.querySelector('.kg-callout-text');
            const body = (emoji ? inlineText(emoji) + ' ' : '') + (text ? inlineText(text) : inlineText(el));
            return ['> ' + body.trim()];
        }
        if (/\bkg-toggle-card\b/.test(classes)) {
            const heading = el.querySelector('.kg-toggle-heading-text');
            const content = el.querySelector('.kg-toggle-content');
            return [heading ? '**' + inlineText(heading) + '**' : ''].concat(content ? renderBlocks(content) : []);
        }
        if (/\bkg-button-card\b/.test(classes)) {
            const link = el.querySelector('a');
            return [link ? inline(link) : ''];
        }
        if (/\bkg-video-card\b/.test(classes)) {
            const video = el.querySelector('video');
            const src = video ? (video.getAttribute('src') || (video.querySelector('source') || {getAttribute: function () {
                return '';
            }}).getAttribute('src')) : '';
            return [src ? '[' + src + '](' + src + ')' : '', caption(el)];
        }
        if (/\bkg-embed-card\b/.test(classes)) {
            const tweet = el.querySelector('blockquote.twitter-tweet');
            if (tweet) {
                return [renderTweet(tweet), caption(el)];
            }
            const iframe = el.querySelector('iframe');
            if (iframe && iframe.getAttribute('src')) {
                const src = iframe.getAttribute('src');
                return ['[' + src + '](' + src + ')', caption(el)];
            }
            return renderBlocks(el);
        }
        if (/\bkg-gallery-card\b/.test(classes)) {
            const images = Array.prototype.map.call(el.querySelectorAll('img'), image).filter(Boolean);
            return [images.join('\n'), caption(el)];
        }
        if (/\bkg-image-card\b/.test(classes) || el.tagName === 'FIGURE') {
            const img = el.querySelector('img');
            if (img) {
                return [image(img), caption(el)];
            }
        }
        return renderBlocks(el);
    }

    /**
     * ブロック要素 1 つを Markdown のブロック配列にする。
     *
     * @param {Element} el
     * @returns {string[]}
     */
    function renderBlock(el) {
        if (SKIP_TAGS.indexOf(el.tagName) >= 0) {
            return [];
        }
        if (/\bkg-card\b/.test(el.className || '') || el.tagName === 'FIGURE') {
            return renderCard(el);
        }
        switch (el.tagName) {
        case 'H1':
        case 'H2':
        case 'H3':
        case 'H4':
        case 'H5':
        case 'H6':
            return ['#'.repeat(Number(el.tagName.charAt(1))) + ' ' + inlineText(el)];
        case 'P':
        case 'FIGCAPTION':
            return [inlineText(el)];
        case 'UL':
        case 'OL':
            return [renderList(el, '')];
        case 'BLOCKQUOTE':
            return [renderBlocks(el).join('\n\n').split('\n').map(function (line) {
                return line ? '> ' + line : '>';
            }).join('\n')];
        case 'HR':
            return ['---'];
        case 'PRE':
            return [renderCode(el)];
        case 'TABLE':
            return [renderTable(el)];
        case 'IFRAME': {
            const src = el.getAttribute('src') || '';
            return [src ? '[' + src + '](' + src + ')' : ''];
        }
        case 'VIDEO': {
            const src = el.getAttribute('src') || '';
            return [src ? '[' + src + '](' + src + ')' : ''];
        }
        default:
            return renderBlocks(el);
        }
    }

    /**
     * 記事本文の要素を Markdown にする。
     *
     * @param {Element} root 本文のルート(.gh-content など)
     * @returns {string}
     */
    function htmlToMarkdown(root) {
        return renderBlocks(root).join('\n\n').trim();
    }

    // -----------------------------------------------------------------------
    // 関連記事と文書の組み立て
    // -----------------------------------------------------------------------

    /**
     * 記事のタイトルと URL をコピー用の文字列にする。
     *
     * @param {string} title
     * @param {string} url
     * @returns {string}
     */
    function buildCopyText(title, url) {
        return title + '\n' + url;
    }

    /**
     * graph.json の記事配列から、現在記事と関係のある記事(引用・被引用・研究者の注釈の双方向)を集める。
     * 同じ記事は 1 件に統合し、関係の種類と理由を relations に並べ、公開日の昇順に返す。
     * 注釈の関係に強さ(graph.json の inferredRefs[].strength。0〜1)があれば、記事ごとの最大値を strength に入れる。
     * 強さは公開記事どうしの関係にしか無いので、無ければ null(強さ不明)にする。
     *
     * @param {Array<{slug: string, title: string, url: string, publishedAt: string, refs?: string[], inferredRefs?: Array<{slug: string, type: string, reason: string|null, strength?: number}>}>} posts
     * @param {string} currentSlug
     * @returns {Array<{slug: string, title: string, url: string, publishedAt: string, relations: Array<{type: string, reason: string|null}>, strength: number|null}>}
     */
    function collectRelated(posts, currentSlug) {
        if (!Array.isArray(posts)) {
            throw new Error('posts は配列である必要があります');
        }
        const postBySlug = {};
        posts.forEach(function (post) {
            postBySlug[post.slug] = post;
        });
        const current = postBySlug[currentSlug];
        if (!current) {
            return [];
        }
        const related = [];
        const add = function (slug, type, reason, strength) {
            const post = postBySlug[slug];
            if (!post || slug === currentSlug) {
                return;
            }
            let entry = related.find(function (candidate) {
                return candidate.slug === slug;
            });
            if (!entry) {
                entry = {slug: post.slug, title: post.title, url: post.url, publishedAt: post.publishedAt, relations: [], strength: null};
                related.push(entry);
            }
            entry.relations.push({type: type, reason: reason || null});
            if (typeof strength === 'number' && (entry.strength === null || strength > entry.strength)) {
                entry.strength = strength;
            }
        };
        (current.refs || []).forEach(function (slug) {
            add(slug, 'cites', null);
        });
        posts.forEach(function (post) {
            if ((post.refs || []).indexOf(currentSlug) >= 0) {
                add(post.slug, 'cited-by', null);
            }
        });
        (current.inferredRefs || []).forEach(function (ref) {
            add(ref.slug, ref.type, ref.reason, ref.strength);
        });
        posts.forEach(function (post) {
            (post.inferredRefs || []).forEach(function (ref) {
                if (ref.slug === currentSlug) {
                    add(post.slug, REVERSE_TYPE[ref.type] || ref.type + '-by', ref.reason, ref.strength);
                }
            });
        });
        return related.sort(function (a, b) {
            return a.publishedAt < b.publishedAt ? -1 : (a.publishedAt > b.publishedAt ? 1 : 0);
        });
    }

    /**
     * 関連記事(collectRelated の結果)を優先度の高い順に並べ、上位 limit 件に絞る。
     * 優先度は「著者の引用(cites / cited-by)がある → 注釈の関係が強い → 強さ不明」の順で、同じ優先度は公開日の古い順。
     * 著者の引用は本文リンクという確かな根拠があるので、機械が推定した強さより先に置く。
     *
     * @param {Array<{slug: string, publishedAt: string, relations: Array<{type: string}>, strength: number|null}>} related
     * @param {number} limit 残す件数の上限(1 以上の整数)
     * @returns {Array} related と同じ形の配列(優先度の高い順)
     */
    function selectRelated(related, limit) {
        if (!Number.isInteger(limit) || limit < 1) {
            throw new Error('limit は 1 以上の整数である必要があります');
        }
        const isCited = function (entry) {
            return entry.relations.some(function (relation) {
                return CITATION_TYPES.indexOf(relation.type) >= 0;
            });
        };
        // 強さ不明(null)は、どの強さ(0 以上)よりも後ろに置く
        const strengthOf = function (entry) {
            return entry.strength === null ? -1 : entry.strength;
        };
        return related.slice().sort(function (a, b) {
            if (isCited(a) !== isCited(b)) {
                return isCited(a) ? -1 : 1;
            }
            if (strengthOf(a) !== strengthOf(b)) {
                return strengthOf(b) - strengthOf(a);
            }
            return a.publishedAt < b.publishedAt ? -1 : (a.publishedAt > b.publishedAt ? 1 : 0);
        }).slice(0, limit);
    }

    /**
     * 取得した記事ページの HTML からタイトルと本文の Markdown を取り出す。
     *
     * @param {string} html
     * @returns {{title: string, markdown: string}}
     */
    function extractPost(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('.gh-content');
        if (!content) {
            throw new Error('記事ページに本文(.gh-content)がありません');
        }
        const title = doc.querySelector('.gh-article-title');
        return {
            title: title ? title.textContent.trim() : '',
            markdown: htmlToMarkdown(content)
        };
    }

    /**
     * 関係の並びを文書に載せる説明にする。
     *
     * @param {Array<{type: string, reason: string|null}>} relations
     * @returns {string}
     */
    function describeRelations(relations) {
        return relations.map(function (relation) {
            const label = RELATION_LABELS[relation.type] || relation.type;
            return relation.reason ? label + ' (' + relation.reason + ')' : label;
        }).join('; ');
    }

    /**
     * 記事 1 件を文書の 1 節にする。
     *
     * @param {{title: string, url: string, publishedAt: string, markdown: string, relations?: Array<object>}} post
     * @param {string} headingPrefix
     * @returns {string}
     */
    function section(post, headingPrefix) {
        const meta = ['- URL: ' + post.url, '- Published: ' + String(post.publishedAt || '').slice(0, 10)];
        if (post.relations && post.relations.length > 0) {
            meta.push('- Relation: ' + describeRelations(post.relations));
        }
        return ['# ' + headingPrefix + post.title, '', meta.join('\n'), '', post.markdown].join('\n');
    }

    /**
     * AI に渡す文書を組み立てる。現在記事の後に、関連記事を区切り線で分けて並べる。
     *
     * @param {{title: string, url: string, publishedAt: string, markdown: string}} current
     * @param {Array<{title: string, url: string, publishedAt: string, markdown: string, relations: Array<{type: string, reason: string|null}>}>} related
     * @returns {string}
     */
    function buildDocument(current, related) {
        return [section(current, '')].concat((related || []).map(function (post) {
            return section(post, 'Related post: ');
        })).join('\n\n---\n\n');
    }

    // -----------------------------------------------------------------------
    // ブラウザでの動作
    // -----------------------------------------------------------------------

    /**
     * graph.json を取得して記事配列を返す。
     *
     * @param {string} url
     * @returns {Promise<Array<object>>}
     */
    function loadPosts(url) {
        if (!url) {
            return Promise.reject(new Error('data-strata-graph-url が設定されていません'));
        }
        return fetch(url).then(function (response) {
            if (!response.ok) {
                throw new Error('graph.json の取得に失敗しました: ' + response.status + ' ' + url);
            }
            return response.json();
        }).then(function (data) {
            if (!data || !Array.isArray(data.posts)) {
                throw new Error('graph.json に posts 配列がありません');
            }
            return data.posts;
        });
    }

    /**
     * 配列の各要素に非同期の処理を適用し、同時実行数を limit までに抑えて入力と同じ順で結果を返す。
     * 1 件でも失敗すればその失敗で reject する(Promise.all と同じ)。
     *
     * @template T, R
     * @param {T[]} items
     * @param {number} limit 同時実行数の上限(1 以上)
     * @param {(item: T) => Promise<R>} worker
     * @returns {Promise<R[]>}
     */
    function mapWithLimit(items, limit, worker) {
        const results = new Array(items.length);
        let next = 0;
        const run = function () {
            if (next >= items.length) {
                return Promise.resolve();
            }
            const index = next;
            next += 1;
            return Promise.resolve(items[index]).then(worker).then(function (result) {
                results[index] = result;
                return run();
            });
        };
        const workers = [];
        for (let i = 0; i < Math.max(1, limit); i += 1) {
            workers.push(run());
        }
        return Promise.all(workers).then(function () {
            return results;
        });
    }

    /**
     * 記事ページを現在の origin から取得し、サーバー出力の HTML からタイトルと本文の Markdown を取り出す。
     * 表示中の DOM ではなく取得した HTML を使うのは、X の widgets.js が埋め込みの blockquote を iframe に
     * 置き換える前の本文(投稿のテキスト)を得るため。DOMParser はスクリプトを実行しない。
     *
     * @param {string} path 記事の pathname
     * @returns {Promise<{title: string, markdown: string}>}
     */
    function loadPostContent(path) {
        return fetch(path).then(function (response) {
            if (!response.ok) {
                throw new Error('記事ページの取得に失敗しました: ' + response.status + ' ' + path);
            }
            return response.text();
        }).then(extractPost);
    }

    /**
     * 関連記事のページを取得し、本文の Markdown を添える。
     * 同時に fetch する数は MAX_CONCURRENT_FETCHES までに抑える。
     *
     * @param {Array<{url: string}>} related
     * @returns {Promise<Array<object>>}
     */
    function loadRelatedContent(related) {
        return mapWithLimit(related, MAX_CONCURRENT_FETCHES, function (post) {
            return loadPostContent(new URL(post.url, window.location.href).pathname).then(function (extracted) {
                return Object.assign({}, post, {
                    title: extracted.title || post.title,
                    markdown: extracted.markdown
                });
            });
        });
    }

    /**
     * 文字列(の Promise)をクリップボードへ書く。
     *
     * @param {Promise<string>} textPromise
     * @returns {Promise<void>}
     */
    function writeClipboard(textPromise) {
        if (!navigator.clipboard) {
            return Promise.reject(new Error('このブラウザではクリップボードに書き込めません'));
        }
        if (window.ClipboardItem && navigator.clipboard.write) {
            const item = new ClipboardItem({
                'text/plain': textPromise.then(function (text) {
                    return new Blob([text], {type: 'text/plain'});
                })
            });
            return navigator.clipboard.write([item]).catch(function (error) {
                // ClipboardItem に Promise を渡せないブラウザでは取得を待ってから writeText で書く。
                // ユーザー操作から離れすぎると Safari はこちらを拒否するため、ClipboardItem を第一候補にしている。
                // 文書の組み立て(textPromise)自体が失敗した場合は再試行せず、その失敗をそのまま伝える
                return textPromise.then(function (text) {
                    console.error('ClipboardItem での書き込みに失敗したため writeText で再試行します', error);
                    return navigator.clipboard.writeText(text);
                });
            });
        }
        return textPromise.then(function (text) {
            return navigator.clipboard.writeText(text);
        });
    }

    /**
     * ボタンの表示を一時的に「Copied」または「Copy failed」にする。
     *
     * @param {HTMLButtonElement} button
     * @param {string} label
     */
    function feedback(button, label) {
        const span = button.querySelector('span') || button;
        if (!button.dataset.originalLabel) {
            button.dataset.originalLabel = span.textContent;
        }
        span.textContent = label;
        window.clearTimeout(Number(button.dataset.feedbackTimer));
        button.dataset.feedbackTimer = String(window.setTimeout(function () {
            span.textContent = button.dataset.originalLabel;
        }, FEEDBACK_MS));
    }

    /**
     * ボタンにコピー処理を結びつける。
     *
     * @param {HTMLElement} sectionEl
     * @param {HTMLButtonElement} button
     * @param {() => Promise<string>} makeText
     */
    function bindCopy(sectionEl, button, makeText) {
        button.disabled = false;
        button.addEventListener('click', function () {
            writeClipboard(makeText()).then(function () {
                feedback(button, sectionEl.dataset.labelCopied || 'Copied');
            }).catch(function (error) {
                console.error(error);
                feedback(button, sectionEl.dataset.labelFailed || 'Copy failed');
            });
        });
    }

    function init() {
        const sectionEl = document.querySelector('[data-post-share]');
        const content = document.querySelector('.gh-content');
        if (!sectionEl || !content) {
            return;
        }
        const current = {
            title: sectionEl.dataset.title || '',
            url: sectionEl.dataset.url || '',
            publishedAt: sectionEl.dataset.published || ''
        };
        // 現在記事の本文も表示中の DOM ではなく自ページを取得して変換する(loadPostContent のコメントを参照)
        const currentMarkdown = function () {
            return loadPostContent(window.location.pathname).then(function (extracted) {
                return extracted.markdown;
            });
        };

        const copyButton = sectionEl.querySelector('[data-share-copy]');
        if (copyButton) {
            bindCopy(sectionEl, copyButton, function () {
                return Promise.resolve(buildCopyText(current.title, current.url));
            });
        }

        const toggles = Array.prototype.slice.call(sectionEl.querySelectorAll('[data-share-toggle]'));
        const singleButton = sectionEl.querySelector('[data-share-ai="single"]');
        const relatedButton = sectionEl.querySelector('[data-share-ai="related"]');
        const relatedLabel = sectionEl.querySelector('[data-share-ai-related-label]');
        if (toggles.length === 0 || !singleButton || !relatedButton) {
            return;
        }

        let relatedPromise = null;
        const loadRelated = function () {
            if (!relatedPromise) {
                relatedPromise = loadPosts(sectionEl.dataset.strataGraphUrl).then(function (posts) {
                    return selectRelated(collectRelated(posts, sectionEl.dataset.currentSlug || ''), RELATED_LIMIT);
                });
                relatedPromise.then(function (related) {
                    if (related.length === 0) {
                        relatedButton.disabled = true;
                        if (relatedLabel) {
                            relatedLabel.textContent = sectionEl.dataset.labelNoRelated || 'No related posts';
                        }
                    } else if (relatedLabel) {
                        relatedLabel.textContent = (sectionEl.dataset.labelRelated || 'This post and % related posts').replace('%', String(related.length));
                    }
                }).catch(function (error) {
                    console.error(error);
                    relatedButton.disabled = true;
                });
            }
            return relatedPromise;
        };

        /**
         * 選択肢(data-share-options)を開閉する。開くときは他の選択肢を閉じ、
         * AI の選択肢を開いたときは関連記事の件数を読み込む。
         *
         * @param {string} name 開閉する選択肢の名前(post / ai)
         * @param {boolean} open
         */
        const setOpen = function (name, open) {
            const toggle = sectionEl.querySelector('[data-share-toggle="' + name + '"]');
            const options = sectionEl.querySelector('[data-share-options="' + name + '"]');
            if (!toggle || !options) {
                return;
            }
            options.hidden = !open;
            toggle.setAttribute('aria-expanded', String(open));
            if (open && name === 'ai') {
                loadRelated();
            }
        };

        const closeAll = function () {
            toggles.forEach(function (toggle) {
                setOpen(toggle.dataset.shareToggle, false);
            });
        };

        toggles.forEach(function (toggle) {
            const name = toggle.dataset.shareToggle;
            toggle.disabled = false;
            toggle.addEventListener('click', function () {
                const open = toggle.getAttribute('aria-expanded') !== 'true';
                closeAll();
                setOpen(name, open);
            });
        });

        // 選択肢はボタンの下に重ねて出すため、外側のクリックと Escape で閉じる
        document.addEventListener('click', function (event) {
            if (!sectionEl.querySelector('[data-share-options]:not([hidden])')) {
                return;
            }
            if (!event.target.closest || !event.target.closest('.gh-post-share-menu')) {
                closeAll();
            }
        });
        document.addEventListener('keydown', function (event) {
            if (event.key === 'Escape') {
                closeAll();
            }
        });

        bindCopy(sectionEl, singleButton, function () {
            return currentMarkdown().then(function (markdown) {
                return buildDocument(Object.assign({markdown: markdown}, current), []);
            });
        });
        bindCopy(sectionEl, relatedButton, function () {
            return Promise.all([currentMarkdown(), loadRelated().then(loadRelatedContent)]).then(function (results) {
                return buildDocument(Object.assign({markdown: results[0]}, current), results[1]);
            });
        });
    }

    if (typeof window !== 'undefined') {
        window.HanataneShare = {
            htmlToMarkdown: htmlToMarkdown,
            collectRelated: collectRelated,
            selectRelated: selectRelated,
            buildDocument: buildDocument,
            buildCopyText: buildCopyText,
            extractPost: extractPost,
            loadPostContent: loadPostContent,
            mapWithLimit: mapWithLimit
        };
        if (typeof document !== 'undefined' && document.querySelector) {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', init);
            } else {
                init();
            }
        }
    }
})();
